import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, Package, MapPin, Save, Mail, CheckCircle2, Minus, Plus, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const eur = (n) => `€ ${Number(n).toFixed(2)}`;
const fmt = (d) => { try { return new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return d; } };

const PAY_BADGE = {
  pagato: "bg-emerald-100 text-emerald-800",
  in_attesa: "bg-amber-100 text-amber-800",
  da_pagare_al_ritiro: "bg-amber-100 text-amber-800",
  annullato: "bg-red-100 text-red-700",
  rimborsato: "bg-gray-200 text-gray-700",
};
const PREP_BADGE = {
  da_preparare: "bg-gray-100 text-gray-700",
  in_preparazione: "bg-blue-100 text-blue-800",
  pronto: "bg-emerald-100 text-emerald-800",
  ritirato: "bg-[#2E4F3B] text-white",
};
const PREP_LABEL = { da_preparare: "Da preparare", in_preparazione: "In preparazione", pronto: "Pronto", ritirato: "Ritirato" };
const FILTERS = [["", "Tutti"], ["da_preparare", "Da preparare"], ["in_preparazione", "In preparazione"], ["pronto", "Pronto"], ["ritirato", "Ritirato"]];

const OrderCard = ({ order, token, onChange }) => {
  const hdr = { "Content-Type": "application/json", "X-Admin-Password": token };
  const paid = order.payment_status === "pagato";
  const preparedTotal = (order.items || []).reduce((s, l) => s + (l.prepared_qty || 0), 0);
  const totalQty = (order.items || []).reduce((s, l) => s + l.quantity, 0);
  const allPrepared = preparedTotal >= totalQty;

  const setItemPrep = async (idx, val) => {
    const r = await fetch(`${API}/api/admin/shop-orders/${order.id}/item-prep`, { method: "PUT", headers: hdr, body: JSON.stringify({ item_index: idx, prepared_qty: val }) });
    if (r.ok) onChange(); else toast.error("Errore aggiornamento.");
  };
  const markReady = async () => {
    if (!window.confirm("Confermi che tutto l'ordine è disponibile presso il punto di ritiro? Verrà inviata l'email al cliente.")) return;
    const r = await fetch(`${API}/api/admin/shop-orders/${order.id}/ready`, { method: "POST", headers: hdr });
    const d = await r.json();
    if (!r.ok) { toast.error(d.detail || "Errore."); return; }
    toast.success(d.email_sent ? "Email 'pronto al ritiro' inviata!" : (d.note || "Aggiornato."));
    onChange();
  };
  const markPickedUp = async () => {
    const r = await fetch(`${API}/api/admin/shop-orders/${order.id}/picked-up`, { method: "POST", headers: hdr });
    if (r.ok) { toast.success("Ordine segnato come ritirato."); onChange(); } else toast.error("Errore.");
  };

  return (
    <div data-testid={`order-${order.order_number}`} className="rounded-2xl border-2 border-[#E5E1D8] bg-white p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display font-black text-[#1A1A1A]">{order.order_number}</div>
          <div className="text-xs text-[#9a9a9a]">{fmt(order.created_at)}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${PAY_BADGE[order.payment_status] || "bg-gray-100"}`}>{paid ? "PAGATO" : order.payment_status}</span>
          <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${PREP_BADGE[order.preparation_status] || "bg-gray-100"}`}>{PREP_LABEL[order.preparation_status] || order.preparation_status}</span>
        </div>
      </div>

      <div className="mt-3 text-sm">
        <div className="font-semibold text-[#1A1A1A]">{order.first_name} {order.last_name}</div>
        <div className="text-[#5C5C5C]">{order.email} · <span className="font-mono">{order.phone}</span></div>
      </div>

      <div className="mt-3 space-y-2">
        {(order.items || []).map((l, i) => {
          const ready = (l.prepared_qty || 0) >= l.quantity;
          return (
            <div key={i} className="flex items-center gap-2 bg-[#FBF9F6] rounded-xl p-2.5">
              <span className="flex-1 text-sm">{l.quantity}× Maglietta{l.size ? ` · ${l.size}` : ""}</span>
              {order.preparation_status !== "ritirato" && (
                <div className="inline-flex items-center gap-1 bg-white border border-[#E5E1D8] rounded-lg p-0.5">
                  <button data-testid={`order-${order.order_number}-item-${i}-minus`} onClick={() => setItemPrep(i, Math.max(0, (l.prepared_qty || 0) - 1))} className="w-7 h-7 rounded flex items-center justify-center hover:bg-[#FBF9F6]"><Minus className="w-3.5 h-3.5" /></button>
                  <span className={`w-12 text-center text-xs font-bold ${ready ? "text-emerald-700" : ""}`}>{l.prepared_qty || 0}/{l.quantity}</span>
                  <button data-testid={`order-${order.order_number}-item-${i}-plus`} onClick={() => setItemPrep(i, Math.min(l.quantity, (l.prepared_qty || 0) + 1))} className="w-7 h-7 rounded flex items-center justify-center hover:bg-[#FBF9F6]"><Plus className="w-3.5 h-3.5" /></button>
                </div>
              )}
              {ready && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <span className="text-sm text-[#5C5C5C]">Preparate {preparedTotal}/{totalQty}</span>
        <span className="font-display text-lg font-black">{eur(order.total)}</span>
      </div>

      {order.preparation_status !== "ritirato" ? (
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <button
            data-testid={`order-${order.order_number}-ready`}
            onClick={markReady}
            disabled={!paid || !allPrepared}
            title={!paid ? "Solo per ordini pagati" : !allPrepared ? "Prepara tutte le magliette" : ""}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#2E4F3B] hover:bg-[#1f3828] disabled:opacity-40 text-white font-bold px-4 py-2.5 text-sm transition-colors">
            <Mail className="w-4 h-4" /> Pronto per il ritiro e invia email
          </button>
          {order.preparation_status === "pronto" && (
            <button data-testid={`order-${order.order_number}-pickedup`} onClick={markPickedUp} className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#2E4F3B] text-[#2E4F3B] font-bold px-4 py-2.5 text-sm hover:bg-[#2E4F3B] hover:text-white transition-colors"><CheckCircle2 className="w-4 h-4" /> Ritirato</button>
          )}
        </div>
      ) : <div className="mt-3 text-sm text-[#2E4F3B] font-bold flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Consegnato</div>}

      {order.emails?.ready_sent && <div className="mt-2 text-[11px] text-[#9a9a9a]">✉︎ Email di ritiro inviata</div>}
    </div>
  );
};

const PickupSettings = ({ token }) => {
  const hdr = { "Content-Type": "application/json", "X-Admin-Password": token };
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { fetch(`${API}/api/admin/shop-settings`, { headers: hdr }).then((r) => r.json()).then(setS).catch(() => {}); }, []); // eslint-disable-line
  if (!s) return null;
  const save = async () => {
    setSaving(true);
    const r = await fetch(`${API}/api/admin/shop-settings`, { method: "PUT", headers: hdr, body: JSON.stringify(s) });
    setSaving(false);
    if (r.ok) toast.success("Punto di ritiro salvato."); else toast.error("Errore salvataggio.");
  };
  const tid = (k) => ({ pickup_name: "pickup-name", pickup_address: "pickup-address", pickup_hours: "pickup-hours", pickup_map_url: "pickup-map-url" }[k] || `pickup-${k}`);
  const f = (k, ph) => <input data-testid={tid(k)} value={s[k] || ""} onChange={(e) => setS((v) => ({ ...v, [k]: e.target.value }))} placeholder={ph} className="w-full bg-white border-2 border-[#E5E1D8] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#2E4F3B]" />;
  return (
    <div className="rounded-2xl border-2 border-[#E5E1D8] bg-[#FBF9F6] p-4 mb-5">
      <div className="flex items-center gap-2 mb-3"><MapPin className="w-4 h-4 text-[#2E4F3B]" /><h4 className="font-bold text-[#1A1A1A]">Punto di ritiro (usato nelle email)</h4></div>
      <div className="grid sm:grid-cols-2 gap-2">
        {f("pickup_name", "Denominazione (es. Edicola della Patrizia)")}
        {f("pickup_address", "Indirizzo completo *")}
        {f("pickup_hours", "Orari (facoltativo)")}
        {f("pickup_map_url", "Link mappa")}
      </div>
      <button data-testid="pickup-save" onClick={save} disabled={saving} className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#2E4F3B] text-white font-bold px-5 py-2.5 text-sm disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva punto di ritiro
      </button>
      {!s.pickup_address && <div className="mt-2 text-[11px] text-[#C15C3D]">⚠︎ Inserisci l'indirizzo: l'email "pronto al ritiro" è bloccata finché è vuoto.</div>}
    </div>
  );
};

export const AdminOrders = ({ token }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter) params.set("preparation", filter);
    if (q.trim()) params.set("q", q.trim());
    try {
      const r = await fetch(`${API}/api/admin/shop-orders?${params}`, { headers: { "X-Admin-Password": token } });
      setOrders(await r.json());
    } catch { /* noop */ } finally { setLoading(false); }
  }, [filter, q, token]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-orders" className="rounded-3xl border-2 border-[#E5E1D8] bg-white p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Package className="w-5 h-5 text-[#2E4F3B]" /><h3 className="font-display text-xl font-black text-[#1A1A1A]">Ordini magliette</h3></div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-[#FBF9F6]" title="Aggiorna"><RefreshCw className="w-4 h-4" /></button>
      </div>

      <PickupSettings token={token} />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-[#9a9a9a] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="orders-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome o numero ordine…" className="w-full bg-white border-2 border-[#E5E1D8] rounded-xl pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#2E4F3B]" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-5">
        {FILTERS.map(([val, lab]) => (
          <button key={val} data-testid={`orders-filter-${val || "all"}`} onClick={() => setFilter(val)} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${filter === val ? "bg-[#1A1A1A] text-white" : "bg-[#FBF9F6] border border-[#E5E1D8] text-[#1A1A1A] hover:border-[#1A1A1A]"}`}>{lab}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-[#5C5C5C]"><Loader2 className="w-5 h-5 animate-spin mr-2" />Caricamento…</div>
      ) : orders.length === 0 ? (
        <p className="text-sm text-[#5C5C5C] italic text-center py-10">Nessun ordine trovato.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {orders.map((o) => <OrderCard key={o.id} order={o} token={token} onChange={load} />)}
        </div>
      )}
    </div>
  );
};
